import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import { isNullOrEmptyArray } from "src/util/arrays";
import { Item } from "../../components";
import * as Ui from "./styles";

const displayModes = {
  vertical: Ui.VerticalContainer,
  horizontal: Ui.HorizontalContainer,
};

function GenericCollapsibleList({
  // Dados que serão renderizados nos itens para controle de estado interno de seleção
  // ou para visualização.
  data = [],
  //
  withControlledItems = false,
  //
  withExpansibleItems = false,
  //
  withRemovableItems = false,
  //
  itemCheckboxStyle,
  //
  onChangeSelection,
  //
  onReady,
  //
  onRemoveItem,
  //
  onExpandChanges,
  //
  containerWidth,
  //
  containerHeight,
  //
  displayMode = "vertical",
  //
  ItemsGap,
  // Define se o filho expandível será renderizado no modo grow | overflow
  // a diferença entre eles é que em grow o container irá ter sua altura expandida junto com o children
  // e em overflow, ele irá sobrepor os elementos inferiores.
  childrenDisplayMode="grow",
  // Propriedades que customizam o item
  customItemProps = {},
  // Componente que será passado como filho de item
  ChildrenComponent,
  //
  itemConditionalRenderer,
  //
  selectedValues = [],
  // Propriedade relacionada ao itens que foram visualizados pelo usuário
  itemsVisualized,
  // outras propriedades
  ...props
}, ref) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [withDisabledSelection, setWithDisabledSelection] = useState(false);

  /**
   * Effects: seta valores de itens em checked/unchecked pre-definidos
   */
  useEffect(() => {
    if (!isNullOrEmptyArray(selectedValues)) {
      setSelectedItems([...selectedValues]);
    }
  }, [selectedValues, setSelectedItems]);

  const instance = {
    // Reseta a listagem de alternativas.
    reset() {
      setSelectedItems([]);
      if (onChangeSelection) onChangeSelection([]);
    },
    // Desabilita todas as checkboxes das alternativas listadas.
    disableSelection() {
      setWithDisabledSelection(true);
    },
    // Habilita todas as checkboxes das alternativas listadas.
    enableSelection() {
      setWithDisabledSelection(false);
    },
  }

  /**
   * Effects: Quando o componente for montado
   */
   useEffect(() => {
    if (onReady) onReady(instance);
  }, []);

  /**
   * Imperative handle: Externalizando métodos úteis
   */
  useImperativeHandle(ref, () => instance);

  const onSelectItem = useCallback(
    (item) => {
      setSelectedItems((state) => {
        let sel = [...state];
        const itemIndex = sel.findIndex((sitem) => sitem.id === item.id);

        // Circle checkbox case
        if (itemCheckboxStyle === "circle" && itemIndex === -1) {
          sel = [{ ...item }];
          if (onChangeSelection) onChangeSelection(sel);

          return sel;
        }

        // Squarecheckbox case
        if (!item.checked && itemIndex !== -1) {
          sel.splice(itemIndex, 1);
        } else if (item.checked) {
          sel.push(item);
        }

        if (onChangeSelection) onChangeSelection(sel);

        return sel;
      });
    },
    [selectedItems, setSelectedItems]
  );

  const handleOnRemoveItem = useCallback((item) => {
    const newState = [...removedItems, { ...item }];

    setRemovedItems(newState);

    setSelectedItems((state) => {
      const temp = [...state];
      const itemIndex = temp.indexOf(item);

      temp.splice(itemIndex, 1);

      if (onChangeSelection) onChangeSelection(temp);

      return temp;
    });

    if (onRemoveItem) onRemoveItem(newState);
  }, [removedItems, setRemovedItems, selectedItems, setSelectedItems]);

  const onExpandItem = useCallback(
    (item, isExpanded) => {
      setExpandedItems((state) => {
        const expanded = [...state];
        const itemIndex = expanded.findIndex((sitem) => sitem.id === item.id);

        if (!isExpanded && itemIndex !== -1) {
          expanded.splice(itemIndex, 1);
        } else if (isExpanded) {
          expanded.push(item);
        }

        if (onExpandChanges) onExpandChanges(expanded);

        return expanded;
      });
    },
    [expandedItems, setExpandedItems]
  );

  const getContainerByDisplayMode = useCallback(() => {
    if (displayModes[displayMode]) return displayModes[displayMode];
  }, [displayMode]);

  const Container = getContainerByDisplayMode();

  const renderItems = useCallback(() => {
    return data.map(({ mainTitle, tagTitle, id, ...rest }, index) => {
      const decIndex = 1 + data.length - index;

      return (
        <Item
          key={`key-${index}-${id}`}
          id={id}
          mainTitle={mainTitle}
          tagTitle={tagTitle}
          selectable={withControlledItems}
          expansible={withExpansibleItems}
          removable={withRemovableItems}
          checkboxStyle={itemCheckboxStyle}
          handleOnSelectItem={withControlledItems && onSelectItem}
          onRemove={withRemovableItems && handleOnRemoveItem}
          disabledCheckbox={withDisabledSelection}
          isSelected={selectedItems.find((item) => item.id === id)}
          onExpand={withExpansibleItems && onExpandItem}
          childrenDisplayMode={childrenDisplayMode}
          Children={withExpansibleItems && ChildrenComponent}
          zIndex={decIndex}
          isOpen={itemsVisualized && itemsVisualized.some(item => item.id === id)}
          {...(itemConditionalRenderer && itemConditionalRenderer({ mainTitle, id, ...rest }))}
          {...customItemProps}
        >
        </Item>
      )
    });
  }, [data, customItemProps, selectedItems, withDisabledSelection]);

  return <Container>{renderItems()}</Container>;
}

export default forwardRef(GenericCollapsibleList);
